import {
  computePosition,
  flip,
  shift,
  offset,
  autoUpdate,
  type Placement,
} from '@floating-ui/dom';
import {
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
  type ReactElement,
} from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import styles from './floating-tooltip.module.scss';

export interface FloatingTooltipProps {
  /**
   * The trigger element content (e.g., an info icon)
   */
  children: ReactNode;
  /**
   * The tooltip content (string or ReactNode)
   */
  content: ReactNode;
  /**
   * Additional CSS class for the tooltip container
   */
  className?: string;
  /**
   * Additional CSS class for the trigger element
   */
  triggerClassName?: string;
  /**
   * Preferred placement of the tooltip relative to the trigger
   * @default 'top'
   */
  placement?: Placement;
  /**
   * Offset distance from the trigger in pixels
   * @default 8
   */
  offsetDistance?: number;
  /**
   * Padding from viewport edges in pixels
   * @default 8
   */
  shiftPadding?: number;
  /**
   * When true, merges tooltip behavior onto the child element instead of wrapping in a span.
   * Useful when wrapping would break layout (e.g., buttons in flex containers).
   * The child must be a single React element that accepts ref and event handlers.
   * @default false
   */
  asChild?: boolean;
  /**
   * When false, the shown tooltip is not added to the trigger's description,
   * for a trigger already described by an always-present copy of the content.
   * @default true
   */
  describeTrigger?: boolean;
}

/**
 * Points a callback or object ref at a node.
 *
 * @param {React.Ref<HTMLElement> | undefined} ref - The ref to set.
 * @param {HTMLElement | null} node - The DOM node.
 */
function assignRef(
  ref: React.Ref<HTMLElement> | undefined,
  node: HTMLElement | null
) {
  if (typeof ref === 'function') {
    ref(node);
  } else if (ref) {
    (ref as React.MutableRefObject<HTMLElement | null>).current = node;
  }
}

/**
 * Shared primitive for tooltips that need to escape overflow boundaries.
 * Uses floating-ui for positioning and React portals to render to document.body.
 *
 * This solves the common problem where tooltips are clipped by parent containers
 * with overflow: hidden/auto. By portaling to document.body, tooltips can render
 * above all other content regardless of their parent's overflow constraints.
 *
 * @param {FloatingTooltipProps} props - Component props
 * @return {JSX.Element} A trigger element with floating tooltip
 */
export function FloatingTooltip({
  children,
  content,
  className,
  triggerClassName,
  placement = 'top',
  offsetDistance = 8,
  shiftPadding = 8,
  asChild = false,
  describeTrigger = true,
}: FloatingTooltipProps) {
  const tooltipId = useId();
  const [isVisible, setIsVisible] = useState(false);
  const [isPositioned, setIsPositioned] = useState(false);
  const [actualPlacement, setActualPlacement] = useState<Placement>(placement);
  const triggerRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  const isShown = Boolean(isVisible && content);
  const describedByTooltip = describeTrigger && isShown;

  // Showing, hiding or re-placing the tooltip puts it back to unpositioned
  // until floating-ui places it again.
  const positionDeps = `${isShown}|${placement}|${offsetDistance}|${shiftPadding}`;
  const [prevPositionDeps, setPrevPositionDeps] = useState(positionDeps);
  if (prevPositionDeps !== positionDeps) {
    setPrevPositionDeps(positionDeps);
    setIsPositioned(false);
  }

  // Handle tooltip positioning and auto-updates
  useEffect(() => {
    if (isShown) {
      // Use requestAnimationFrame to ensure the portal is rendered before positioning
      const rafId = requestAnimationFrame(() => {
        if (triggerRef.current && contentRef.current) {
          cleanupRef.current = autoUpdate(
            triggerRef.current,
            contentRef.current,
            () => {
              if (triggerRef.current && contentRef.current) {
                void computePosition(triggerRef.current, contentRef.current, {
                  placement,
                  strategy: 'fixed',
                  middleware: [
                    offset(offsetDistance),
                    flip(),
                    shift({ padding: shiftPadding }),
                  ],
                }).then(({ x, y, placement: finalPlacement }) => {
                  if (contentRef.current) {
                    Object.assign(contentRef.current.style, {
                      left: `${x}px`,
                      top: `${y}px`,
                    });
                    setActualPlacement(finalPlacement);
                    setIsPositioned(true);
                  }
                });
              }
            }
          );
        }
      });

      return () => {
        cancelAnimationFrame(rafId);
        if (cleanupRef.current) {
          cleanupRef.current();
          cleanupRef.current = null;
        }
      };
    }
    return undefined;
  }, [isShown, placement, offsetDistance, shiftPadding]);

  // Escape dismisses a shown tooltip, hovered or focused (WCAG 1.4.13). Window
  // capture runs before anything inside the page can stop the key.
  useEffect(() => {
    if (!isShown) return undefined;
    /**
     * Hides the tooltip when Escape is pressed.
     *
     * @param {KeyboardEvent} event - The key press.
     */
    const hideOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsVisible(false);
    };
    window.addEventListener('keydown', hideOnEscape, true);
    return () => window.removeEventListener('keydown', hideOnEscape, true);
  }, [isShown]);

  const tooltipPortal =
    isShown &&
    createPortal(
      <div
        ref={contentRef}
        id={tooltipId}
        className={classNames(styles.tooltip, className, {
          [styles.positioned]: isPositioned,
        })}
        data-placement={actualPlacement.split('-')[0]}
        role="tooltip"
      >
        {content}
      </div>,
      document.body
    );

  if (asChild) {
    const childElement = children as ReactElement;
    // Access ref from element - use type assertion since ref isn't in public ReactElement type
    const childRef = (
      childElement as ReactElement & { ref?: React.Ref<HTMLElement> }
    ).ref;

    return (
      <>
        <childElement.type
          {...childElement.props}
          key={childElement.key}
          ref={(node: HTMLElement | null) => {
            assignRef(triggerRef, node);
            assignRef(childRef, node);
          }}
          onMouseEnter={() => setIsVisible(true)}
          onMouseLeave={() => setIsVisible(false)}
          onFocus={() => setIsVisible(true)}
          onBlur={() => setIsVisible(false)}
          aria-describedby={
            [
              (childElement.props as { 'aria-describedby'?: string })[
                'aria-describedby'
              ],
              describedByTooltip && tooltipId,
            ]
              .filter(Boolean)
              .join(' ') || undefined
          }
        />
        {tooltipPortal}
      </>
    );
  }

  return (
    <>
      <span
        ref={triggerRef}
        className={classNames(styles.trigger, triggerClassName)}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onFocus={() => setIsVisible(true)}
        onBlur={() => setIsVisible(false)}
        tabIndex={0}
        role="button"
        aria-describedby={describedByTooltip ? tooltipId : undefined}
      >
        {children}
      </span>
      {tooltipPortal}
    </>
  );
}
